import { useRef, useMemo, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';

import { useSetState } from 'src/hooks/use-set-state';

import { tokenRefresh } from './action';
import { AuthContext } from '../auth-context';
import { jwtDecode, setSession, isValidToken } from './utils';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from './constant';

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const refreshTimer = useRef(null);

  const { state, setState } = useSetState({
    user: null,
    loading: true,
  });

  const inactivityTimer = useRef(null);
  const AUTO_LOGOUT_TIME = 1000 * 60 * 60;

  const autoLogout = useCallback(() => {
    setSession(null);
    window.alert('You have been logged out due to inactivity.');
    window.location.href = paths.auth.login;
  }, []);

  const resetTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(autoLogout, AUTO_LOGOUT_TIME);
  }, [AUTO_LOGOUT_TIME, autoLogout]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (state.user) {
      const events = ['mousemove', 'keydown', 'click', 'scroll'];
      events.forEach(event => window.addEventListener(event, resetTimer));
      resetTimer();

      return () => {
        events.forEach(event => window.removeEventListener(event, resetTimer));
        clearTimeout(inactivityTimer.current);
      };
    }
  }, [resetTimer, state.user]);

  // ----------------------------------------------------------------------

  const checkUserSession = useCallback(() => {
    try {
      const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
      const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken, refreshToken);
        const decoded = jwtDecode(accessToken);

        const user = { ...decoded, accessToken, Staff: decoded?.Staff ? JSON.parse(decoded.Staff) : null }

        if (process.env.REACT_APP_ENV_TYPE === 'development') console.log(user)

        setState({ user, loading: false });

        scheduleTokenRefresh();
      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setState]);

  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  // ----------------------------------------------------------------------

  const scheduleTokenRefresh = () => {
    const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);

    const { exp } = jwtDecode(accessToken);
    const timeLeft = exp * 1000 - Date.now() - 60000;

    if (refreshTimer.current) clearTimeout(refreshTimer.current);

    refreshTimer.current = setTimeout(async () => {
      try {
        await tokenRefresh({ access_token: accessToken, refresh_token: refreshToken });
        checkUserSession();
        scheduleTokenRefresh();

      } catch (error) {
        console.error('Token refresh failed:', error);
        setState({ user: null, loading: false });
        sessionStorage.clear();
        window.location.href = paths.auth.login;
      }
    }, timeLeft);
  }

  // ----------------------------------------------------------------------

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      setUser: (fields) => setState({ user: { ...state.user, ...fields }, loading: false }),
      checkUserSession,
      loading: state.loading,
      authenticated: !!state.user,
      unauthenticated: !state.user,
    }),
    [checkUserSession, setState, state.loading, state.user]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
